'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';

import { useQuery } from '@tanstack/react-query';
import { User } from 'lucide-react';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { JWTUserData } from '@kit/supabase/types';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({ default: mod.ModeToggle })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface KaramaHeaderAccountSectionProps {
  onNavigate?: () => void;
  /** True when the navbar is transparent over the hero (use light styles). */
  onDark?: boolean;
  /**
   * Compact mode for the mobile/tablet top bar: renders a tighter Book Now +
   * Sign In pair (no theme toggle, no account dropdown) so the CTAs fit
   * alongside the hamburger. The full dropdown/admin actions stay in the
   * desktop bar and the mobile drawer.
   */
  compact?: boolean;
}

export function KaramaHeaderAccountSection({
  onNavigate,
  onDark = false,
  compact = false,
}: KaramaHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  // Compact mobile bar: just Book Now, the primary conversion action stays
  // always-visible next to the hamburger. Sign In (and the full account
  // dropdown / admin actions) live one tap away inside the mobile drawer,
  // which renders the non-compact section. Keeps the narrow top bar uncluttered.
  if (compact) {
    return (
      <Link
        href={paths.bookCourt}
        onClick={onNavigate}
        className="inline-flex h-9 items-center justify-center rounded-full bg-[var(--karama-primary)] px-4 text-xs font-semibold whitespace-nowrap text-white transition-colors hover:bg-[var(--karama-primary-dark)]"
      >
        <Trans i18nKey="common:bookCourt" defaults="Book Now" />
      </Link>
    );
  }

  if (session.data) {
    const role = userRole.data || 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <div className="hidden md:flex">
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex h-10 items-center justify-center rounded-full bg-[var(--karama-primary)] px-5 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-[var(--karama-primary-dark)]"
          >
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className="hidden h-10 items-center justify-center rounded-full border-2 border-[var(--karama-primary)] px-4 text-sm font-semibold whitespace-nowrap text-[var(--karama-primary)] transition-colors hover:bg-[var(--karama-primary)] hover:text-white md:inline-flex"
          >
            <Trans i18nKey="common:adminPanel" defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    if (role === 'user') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <div className="hidden md:flex">
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex h-10 items-center justify-center rounded-full bg-[var(--karama-primary)] px-5 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-[var(--karama-primary-dark)]"
          >
            <Trans i18nKey="common:bookCourt" defaults="Book Now" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  return <AuthButtons onNavigate={onNavigate} onDark={onDark} />;
}

function AuthButtons({
  onNavigate,
  onDark = false,
}: {
  onNavigate?: () => void;
  onDark?: boolean;
}) {
  return (
    <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
      <div className="hidden md:flex">
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className="md:hidden">
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      <Link
        href={paths.bookCourt}
        onClick={onNavigate}
        className="inline-flex h-10 items-center justify-center rounded-full bg-[var(--karama-primary)] px-5 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-[var(--karama-primary-dark)]"
      >
        <Trans i18nKey="common:bookCourt" defaults="Book Now" />
      </Link>

      {/* Sign In is always visible. AuthButtons renders in the mobile drawer
          (where this is the ONLY Sign In entry point) and in the desktop bar;
          a previous `hidden sm:inline-flex` hid it on small phones, leaving the
          drawer with just Book Now. */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className={
          onDark
            ? 'group inline-flex h-10 items-center justify-center gap-2 rounded-full border-2 border-white/70 px-5 text-sm font-semibold whitespace-nowrap text-white transition-all duration-200 hover:-translate-y-0.5 hover:border-white hover:bg-white hover:text-[var(--karama-primary)] hover:shadow-md active:translate-y-0'
            : 'group inline-flex h-10 items-center justify-center gap-2 rounded-full border-2 border-[var(--karama-primary)] px-5 text-sm font-semibold whitespace-nowrap text-[var(--karama-primary)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[var(--karama-primary)] hover:text-white hover:shadow-md active:translate-y-0'
        }
      >
        <User className="h-4 w-4" />
        <Trans i18nKey="auth:signIn" defaults="Sign In" />
      </a>
    </div>
  );
}

function useSession() {
  const client = useSupabase();
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();
  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';
      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);
        if (memberships && memberships.length > 0) {
          if (memberships.some((m) => m.account_role === 'owner'))
            return 'owner';
          return 'member';
        }
        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();
  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(
            `account_id, accounts!accounts_memberships_account_id_fkey!inner (id, slug, name, is_personal_account)`,
          )
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);
        if (memberships && memberships.length > 0) {
          return memberships[0]?.accounts;
        }
        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
